import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { retryUnlessClientError } from "@/lib/queryRetry";
import { setSelectedCrmEnabled } from "@/lib/outletFeatures";

/** GET /outlets/{id}/features: the outlet's feature switches (set by Dhaba support). */
export interface OutletFeaturesResponse {
  outlet_id: string;
  crm_enabled: boolean;
}

/** How often the switches are re-read while the app is open. */
export const OUTLET_FEATURES_REFETCH_MS = 5 * 60_000;

/**
 * Keeps the selected outlet's feature switches current: read on mount, every
 * 5 minutes and when the window regains focus. Pass null when no outlet staff
 * session is selected (nothing is fetched).
 */
export function useOutletFeatures(outletId: string | null | undefined) {
  return useQuery({
    queryKey: ["outlet-features", outletId],
    queryFn: async () => {
      const features = await api.get<OutletFeaturesResponse>(`/api/v1/outlets/${outletId}/features`);
      setSelectedCrmEnabled(features?.crm_enabled, outletId as string);
      return features;
    },
    enabled: !!outletId,
    refetchInterval: OUTLET_FEATURES_REFETCH_MS,
    refetchOnWindowFocus: true,
    retry: retryUnlessClientError,
  });
}
